import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  TextField,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import InfoIcon from '@mui/icons-material/Info'
import HistoryIcon from '@mui/icons-material/History'
import ErrorIcon from '@mui/icons-material/Error'
import { locationService } from '../services'
import { formatDateTime } from '../utils/dateFormat'
import { getUploadSuccessMessage } from '../utils/uploadSuccessMessages'
import UploadErrorDetailsDialog from '../components/UploadErrorDetailsDialog'
import UploadHistoryRefreshButton from '../components/UploadHistoryRefreshButton'
import UploadHistoryTableWrapper from '../components/UploadHistoryTableWrapper'
import UploadImportStatusAlert from '../components/UploadImportStatusAlert'
import InstructionCard from '../components/common/InstructionCard'
import { locationUploadInstructions } from '../constants/uploadInstructions'
import { useUploadHistoryPolling } from '../hooks/useUploadHistoryPolling'

const LocationUploadPage = () => {
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [batchSize, setBatchSize] = useState(50)
  const [activeTab, setActiveTab] = useState(0)
  const [isDragOver, setIsDragOver] = useState(false)

  // Upload history states
  const [uploadLogs, setUploadLogs] = useState([])
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [errorDetailsDialog, setErrorDetailsDialog] = useState({ open: false, uploadLog: null })
  const [logsPage, setLogsPage] = useState(0)
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10)
  const [logsTotalCount, setLogsTotalCount] = useState(0)
  const logsFetchInFlightRef = useRef(false)

  const fetchUploadLogs = useCallback(async ({ silent = false } = {}) => {
    if (logsFetchInFlightRef.current) {
      return
    }
    logsFetchInFlightRef.current = true
    try {
      if (!silent) {
        setLoadingLogs(true)
      }
      const response = await locationService.getUploadLogs({
        page: logsPage + 1,
        limit: logsRowsPerPage,
      })

      const logsData = response.items || response.data?.items || response.data || response.logs || response
      const totalCount = response.pagination?.total || response.data?.pagination?.total || response.totalCount || response.total || (Array.isArray(logsData) ? logsData.length : 0)

      setUploadLogs(Array.isArray(logsData) ? logsData : [])
      setLogsTotalCount(totalCount)
    } catch (error) {
      console.error('Error fetching upload logs:', error)
      if (!silent) {
        setUploadLogs([])
        setLogsTotalCount(0)
      }
    } finally {
      if (!silent) {
        setLoadingLogs(false)
      }
      logsFetchInFlightRef.current = false
    }
  }, [logsPage, logsRowsPerPage])

  useEffect(() => {
    if (activeTab === 1) {
      fetchUploadLogs()
    }
  }, [activeTab, logsPage, logsRowsPerPage, fetchUploadLogs])

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    if (newValue === 1) {
      fetchUploadLogs()
    }
  }

  const handleViewUploadHistory = useCallback(() => {
    setActiveTab(1)
    fetchUploadLogs()
  }, [fetchUploadLogs])

  useUploadHistoryPolling(activeTab === 1, fetchUploadLogs)

  const handleShowErrorDetails = async (logId) => {
    try {
      const response = await locationService.getUploadLogDetails(logId)
      setErrorDetailsDialog({ open: true, uploadLog: response.data || response })
    } catch (error) {
      console.error('Error fetching upload log details:', error)
    }
  }

  const handleLogsPageChange = (event, newPage) => {
    setLogsPage(newPage)
  }

  const handleLogsRowsPerPageChange = (event) => {
    setLogsRowsPerPage(parseInt(event.target.value, 10))
    setLogsPage(0)
  }

  const handleFileChange = (e) => {
    setFile(e.target.files[0])
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const droppedFile = files[0]
      if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile)
      } else {
        setMessage({ type: 'error', text: 'Please upload a CSV file only' })
      }
    }
  }

  const handleUpload = async () => {
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a file' })
      return
    }

    try {
      setLoading(true)

      const text = await file.text()
      const lines = text.split('\n').filter(line => line.trim())

      const result = await locationService.importLocations({
        file,
        batchSize: batchSize.toString(),
      })

      if (result.success && result.message) {
        setMessage({
          type: 'success',
          text: result.message || getUploadSuccessMessage('location'),
          showHistoryLink: true,
        })
        setFile(null)
        fetchUploadLogs()
      } else {
        setMessage({ type: 'error', text: 'Failed to start location import' })
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error uploading locations',
      })
    } finally {
      setLoading(false)
    }
  }

  const downloadSampleTemplate = () => {
    const headers = ['name,code,geolocation\n']
    const sampleData = [
      'Gulshan Center,LOC-GUL,"23.7925,90.4078"\n',
      'Banani Depot,LOC-BAN,"23.8225,90.4278"\n'
    ]
    const csvContent = headers.concat(sampleData).join('')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'sample_location.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const StatusChip = ({ status, progress }) => {
    const config = {
      completed: { color: 'success', label: 'Completed' },
      failed: { color: 'error', label: 'Failed' },
      processing: { color: 'warning', label: 'Processing' },
      pending: { color: 'default', label: 'Pending' }
    }
    const currentConfig = config[status] || config.pending
    return (
      <Chip
        label={status === 'processing' && progress !== undefined ? `Processing (${progress}%)` : currentConfig.label}
        color={currentConfig.color}
        size="small"
        sx={{ fontWeight: 'medium' }}
      />
    )
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a1a1a', mb: 1 }}>
          Location Upload
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload and manage global shop locations via CSV
        </Typography>
      </Box>

      {/* Tabs Layout */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{
          '& .MuiTabs-indicator': { backgroundColor: '#e90000' },
          '& .Mui-selected': { color: '#e90000 !important' },
        }}>
          <Tab icon={<CloudUploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />} label="Upload Location" iconPosition="start" />
          <Tab icon={<HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />} label="Upload History" iconPosition="start" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Card sx={{
              borderRadius: 2,
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
            }}>
              <CardContent sx={{ p: 4 }}>
                {message && (
                  <UploadImportStatusAlert
                    severity={message.type}
                    onClose={() => setMessage(null)}
                    showHistoryLink={message.showHistoryLink}
                    onViewUploadHistory={handleViewUploadHistory}
                  >
                    {message.text}
                  </UploadImportStatusAlert>
                )}

                {/* Drag and Drop Zone */}
                <Box
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragOver ? '#e90000' : '#ccc',
                    borderRadius: 2,
                    p: 6,
                    textAlign: 'center',
                    bgcolor: isDragOver ? '#fff5f5' : '#fafafa',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#e90000',
                      bgcolor: '#fff5f5'
                    }
                  }}
                  onClick={() => document.getElementById('csv-upload').click()}
                >
                  <CloudUploadIcon sx={{ fontSize: 64, color: isDragOver ? '#e90000' : '#999', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {isDragOver ? 'Drop file here' : 'Drag & Drop your CSV file here'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    or click to browse from your computer
                  </Typography>

                  <input
                    accept=".csv"
                    style={{ display: 'none' }}
                    id="csv-upload"
                    type="file"
                    onChange={handleFileChange}
                    onClick={(e) => { e.target.value = null }}
                  />

                  {file && (
                    <Chip
                      label={file.name}
                      onDelete={(e) => {
                        e.stopPropagation()
                        setFile(null)
                      }}
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 2 }}
                    />
                  )}
                </Box>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <TextField
                    label="Batch Size"
                    type="number"
                    size="small"
                    value={batchSize}
                    onChange={(e) => setBatchSize(parseInt(e.target.value) || 50)}
                    sx={{ width: 120 }}
                    InputProps={{ inputProps: { min: 10, max: 1000 } }}
                  />
                  <Button
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleUpload()
                    }}
                    disabled={!file || loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                    sx={{
                      bgcolor: '#e90000',
                      '&:hover': { bgcolor: '#cc0000' },
                      px: 4,
                      py: 1.5,
                      fontWeight: 'bold'
                    }}
                  >
                    {loading ? 'Uploading...' : 'Upload File'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <DownloadIcon sx={{ color: '#e90000', fontSize: 40 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Sample CSV
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  Download a sample CSV to see the required format.
                </Typography>

                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  startIcon={<DownloadIcon sx={{ fontSize: 20 }} />}
                  onClick={downloadSampleTemplate}
                  sx={{
                    color: '#e90000',
                    borderColor: '#e90000',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#E8F5E9' },
                    py: 1.2,
                  }}
                >
                  Download Sample
                </Button>
              </CardContent>
            </Card>

            <InstructionCard {...locationUploadInstructions} icon={<InfoIcon />} />
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Card sx={{
          borderRadius: 2,
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                  Upload History
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View the status and details of your recent location uploads
                </Typography>
              </Box>
              <UploadHistoryRefreshButton
                onClick={fetchUploadLogs}
                loading={loadingLogs}
              />
            </Box>

            <UploadHistoryTableWrapper
              loading={loadingLogs}
              isEmpty={uploadLogs.length === 0}
              emptyState={
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <HistoryIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                    No Upload History
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    Your location uploads will appear here after you upload CSV files.
                  </Typography>
                </Box>
              }
            >
              <>
                <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto', boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                        <TableCell sx={{ fontWeight: 600, minWidth: 200, bgcolor: '#f8f9fb' }}>File Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 120, bgcolor: '#f8f9fb' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 100, bgcolor: '#f8f9fb' }} align="center">Total</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 100, bgcolor: '#f8f9fb' }} align="center">Success</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 100, bgcolor: '#f8f9fb' }} align="center">Failed</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 150, bgcolor: '#f8f9fb' }}>Upload Date</TableCell>
                        <TableCell sx={{ fontWeight: 600, minWidth: 100, bgcolor: '#f8f9fb' }} align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadLogs.map((log) => (
                        <TableRow
                          key={log.id}
                          sx={{
                            '&:hover': { bgcolor: '#f8f9fb' },
                            '&:nth-of-type(even)': { bgcolor: '#fafafa' }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CloudUploadIcon sx={{ fontSize: 16, color: '#666' }} />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {log.file_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                              sx={{
                                fontWeight: 500,
                                backgroundColor: log.status === 'completed' ? '#e8f5e9' : log.status === 'processing' ? '#fff3e0' : '#ffebee',
                                color: log.status === 'completed' ? '#2e7d32' : log.status === 'processing' ? '#e65100' : '#d32f2f',
                                border: 'none'
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {log.total_rows}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50' }}>
                              {log.successful_imports}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {log.failed_imports > 0 ? (
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#f44336' }}>
                                {log.failed_imports}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#999' }}>
                                0
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                              {formatDateTime(log.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {log.failed_imports > 0 ? (
                              <Tooltip title="View error details">
                                <IconButton
                                  size="small"
                                  onClick={() => handleShowErrorDetails(log.id)}
                                  sx={{
                                    color: '#f44336',
                                    '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.08)' }
                                  }}
                                >
                                  <ErrorIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Box sx={{ width: 32, height: 32, display: 'inline-block' }} />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={logsTotalCount}
                  page={logsPage}
                  onPageChange={handleLogsPageChange}
                  rowsPerPage={logsRowsPerPage}
                  onRowsPerPageChange={handleLogsRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  sx={{ borderTop: 'none' }}
                />
              </>
            </UploadHistoryTableWrapper>
          </CardContent>
        </Card>
      )}

      {/* Error Details Dialog */}
      <UploadErrorDetailsDialog
        open={errorDetailsDialog.open}
        onClose={() => setErrorDetailsDialog({ open: false, uploadLog: null })}
        uploadLog={errorDetailsDialog.uploadLog}
      />
    </Container>
  )
}

export default LocationUploadPage
